import React from 'react';
import Icon from '../../../components/AppIcon';

const ValidationIndicators = ({ validationData }) => {
  const getValidationIcon = (type) => {
    switch (type) {
      case 'error': return 'XCircle';
      case 'warning': return 'AlertTriangle';
      case 'success': return 'CheckCircle';
      case 'info': return 'Info';
      default: return 'AlertCircle';
    }
  };

  const getValidationColor = (type) => {
    switch (type) {
      case 'error': return 'text-error bg-error/10 border-error/20';
      case 'warning': return 'text-warning bg-warning/10 border-warning/20';
      case 'success': return 'text-success bg-success/10 border-success/20';
      case 'info': return 'text-accent bg-accent/10 border-accent/20';
      default: return 'text-text-secondary bg-muted border-border';
    }
  };

  const getSeverityBadge = (severity) => {
    const colors = {
      high: 'bg-error text-error-foreground',
      medium: 'bg-warning text-warning-foreground',
      low: 'bg-accent text-accent-foreground'
    };
    return colors?.[severity] || colors?.low;
  };

  return (
    <div className="bg-surface border border-border rounded-lg p-6 elevation-1">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-text-primary">Content Validation</h2>
        <div className="flex items-center space-x-2">
          <div className={`w-2 h-2 rounded-full ${
            validationData?.overallScore >= 80 ? 'bg-success' : 
            validationData?.overallScore >= 60 ? 'bg-warning' : 'bg-error'
          }`}></div>
          <span className="text-sm text-text-secondary">
            Score: {validationData?.overallScore}/100
          </span>
        </div>
      </div>
      {/* Overall Status */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-error/10 border border-error/20 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="XCircle" size={16} className="text-error" />
            <span className="text-sm font-medium text-error">Errors</span>
          </div>
          <div className="text-2xl font-semibold text-error">{validationData?.errorCount}</div>
          <div className="text-xs text-error/70">Must fix before approval</div>
        </div>

        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="AlertTriangle" size={16} className="text-warning" />
            <span className="text-sm font-medium text-warning">Warnings</span>
          </div>
          <div className="text-2xl font-semibold text-warning">{validationData?.warningCount}</div>
          <div className="text-xs text-warning/70">Recommended fixes</div>
        </div>

        <div className="bg-success/10 border border-success/20 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="CheckCircle" size={16} className="text-success" />
            <span className="text-sm font-medium text-success">Passed</span>
          </div>
          <div className="text-2xl font-semibold text-success">{validationData?.passedCount}</div>
          <div className="text-xs text-success/70">Validation checks</div>
        </div>
      </div>
      {/* Section-wise Validation */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-text-primary mb-4">Section Validation Status</h3>
        <div className="space-y-3">
          {validationData?.sectionValidation?.map((section, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-muted rounded-lg">
              <div className="flex items-center space-x-3">
                <Icon name={section?.icon} size={16} className="text-text-secondary" />
                <span className="text-sm font-medium text-text-primary">{section?.name}</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  {section?.errors > 0 && (
                    <span className="flex items-center space-x-1 text-error">
                      <Icon name="XCircle" size={12} />
                      <span className="text-xs">{section?.errors}</span>
                    </span>
                  )}
                  {section?.warnings > 0 && (
                    <span className="flex items-center space-x-1 text-warning">
                      <Icon name="AlertTriangle" size={12} />
                      <span className="text-xs">{section?.warnings}</span>
                    </span>
                  )}
                  {section?.errors === 0 && section?.warnings === 0 && (
                    <span className="flex items-center space-x-1 text-success">
                      <Icon name="CheckCircle" size={12} />
                      <span className="text-xs">Valid</span>
                    </span>
                  )}
                </div>
                <div className="w-16 bg-border rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${
                      section?.completeness >= 80 ? 'bg-success' : 
                      section?.completeness >= 60 ? 'bg-warning' : 'bg-error'
                    }`}
                    style={{ width: `${section?.completeness}%` }}
                  ></div>
                </div>
                <span className="text-xs text-text-secondary w-8 text-right">
                  {section?.completeness}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Detailed Issues */}
      <div>
        <h3 className="text-sm font-medium text-text-primary mb-4">Detailed Issues</h3>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {validationData?.detailedIssues?.map((issue, index) => (
            <div key={index} className={`p-3 border rounded-lg ${getValidationColor(issue?.type)}`}>
              <div className="flex items-start space-x-3">
                <Icon name={getValidationIcon(issue?.type)} size={16} className="flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">{issue?.title}</span>
                    <span className={`px-2 py-1 text-xs rounded-full ${getSeverityBadge(issue?.severity)}`}>
                      {issue?.severity}
                    </span>
                  </div>
                  <p className="text-sm opacity-90 mb-2">{issue?.description}</p>
                  <div className="flex items-center space-x-2 text-xs opacity-75">
                    <span>Section: {issue?.section}</span>
                    <span>•</span>
                    <span>Field: {issue?.field}</span>
                  </div>
                  {issue?.suggestion && (
                    <div className="mt-2 p-2 bg-black/5 rounded text-xs">
                      <strong>Suggestion:</strong> {issue?.suggestion}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Quality Score Breakdown */}
      <div className="mt-6 pt-6 border-t border-border">
        <h3 className="text-sm font-medium text-text-primary mb-4">Quality Score Breakdown</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {validationData?.qualityMetrics?.map((metric, index) => (
            <div key={index} className="text-center">
              <div className="text-lg font-semibold text-text-primary">{metric?.score}</div>
              <div className="text-xs text-text-secondary">{metric?.name}</div>
              <div className="w-full bg-border rounded-full h-1 mt-1">
                <div 
                  className={`h-1 rounded-full ${
                    metric?.score >= 80 ? 'bg-success' : 
                    metric?.score >= 60 ? 'bg-warning' : 'bg-error'
                  }`}
                  style={{ width: `${metric?.score}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ValidationIndicators;